import { ChangeAction, EntityType, ChangeItem } from '../types'

export interface DisplayNames {
    valueMessages?: string
    evidence?: string
    activities?: string
    keyEvidenceStatements?: string
}

export const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    if (date.toDateString() === today.toDateString()) {
        return `Today at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    }
    if (date.toDateString() === yesterday.toDateString()) {
        return `Yesterday at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    }
    return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    })
}

export const formatDateHeader = (dateString: string, userName: string): string => {
    let date: Date

    // Check if it's a date-only string (YYYY-MM-DD) or full ISO timestamp
    if (dateString.length === 10 && dateString.includes('-')) {
        // Date-only string: parse as local date to avoid timezone shift
        const [year, month, day] = dateString.split('-').map(Number)
        date = new Date(year, month - 1, day) // month is 0-indexed
    } else {
        // Full timestamp: parse normally
        date = new Date(dateString)
    }

    return `Changes on ${date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    })}, by ${userName}`
}

export const getActionLabel = (
    action: ChangeAction,
    entityType: EntityType,
    metadata?: { rowName?: string; subRowNumber?: number; columnTitle?: string; clonedToTitle?: string },
    newObj?: Record<string, unknown>
): string => {
    const actionLabels: Record<ChangeAction, string> = {
        create: 'Created',
        update: 'Edited',
        delete: 'Deleted',
        link: 'Linked',
        unlink: 'Unlinked',
        lock: 'Locked',
        unlock: 'Unlocked',
        clone: 'Cloned',
        declone: 'Decloned',
        highlight: 'Highlighted',
        unhighlight: 'Unhighlighted',
        reorder: 'Reordered',
        archive: 'Archived',
        unarchive: 'Unarchived',
        pin: 'Pinned',
        unpin: 'Unpinned',
        version_save: 'Version Saved',
    }

    const entityLabels: Record<EntityType, string> = {
        TPPCell: 'Cell',
        TPPColumn: 'Column',
        TPPRow: 'Attribute',
        TPPSection: 'Section',
    }

    const actionLabel = actionLabels[action] || action
    const entityLabel = entityLabels[entityType] || entityType

    // For cell actions, show row name and sub-row if available
    if (entityType === 'TPPCell' && metadata?.rowName) {
        // Build the cell location string (e.g., "Safety" or "Safety (Row 2)")
        const subRowStr = metadata.subRowNumber && metadata.subRowNumber > 1 ? ` (Row ${metadata.subRowNumber})` : ''
        const cellLocation = `${metadata.rowName}${subRowStr}`

        // Special handling for clone action
        if (action === 'clone' && (metadata?.clonedToTitle || (newObj?.clonedToTitle as string))) {
            const targetColumn = metadata?.clonedToTitle || (newObj?.clonedToTitle as string)
            return `${actionLabel}: ${cellLocation} to ${targetColumn}`
        }
        return `${actionLabel}: ${cellLocation}`
    }

    // For column actions, show column title if available
    if (entityType === 'TPPColumn' && metadata?.columnTitle) {
        return `${actionLabel}: ${metadata.columnTitle}`
    }

    return `${actionLabel}: ${entityLabel}`
}

export const getLinkedItemType = (change: ChangeItem, displayNames: DisplayNames = {}): string | null => {
    const newObj = change.newObj as Record<string, unknown[]> | undefined
    const oldObj = change.oldObj as Record<string, unknown[]> | undefined

    if (change.action === 'link' || change.action === 'unlink') {
        const obj = change.action === 'link' ? newObj : oldObj
        if (obj?.linkedValueMessages?.length) return displayNames.valueMessages || 'Value Messages'
        if (obj?.linkedEvidence?.length) return displayNames.evidence || 'Evidence'
        if (obj?.linkedActivities?.length) return displayNames.activities || 'Activities'
        if (obj?.linkedKeyEvidenceStatements?.length)
            return displayNames.keyEvidenceStatements || 'Key Evidence Statements'
    }

    return null
}

interface SlotOrderItem {
    slotId: string
    content: string
}

export interface ChangeDetails {
    items?: string[]
    oldValue?: string
    newValue?: string
    reorderBefore?: string[]
    reorderAfter?: string[]
    cellContent?: string
}

export const getChangeDetails = (change: ChangeItem): ChangeDetails => {
    const details: ChangeDetails = {}
    const newObj = change.newObj as Record<string, unknown> | undefined
    const oldObj = change.oldObj as Record<string, unknown> | undefined
    const metadata = change.metadata

    if (change.action === 'link' || change.action === 'unlink') {
        const linkedItems = (change.action === 'link' ? newObj : oldObj) as Record<string, unknown[]> | undefined

        if (linkedItems) {
            const allItems: string[] = []

            const extractNames = (items: unknown[] | undefined) => {
                if (Array.isArray(items)) {
                    items.forEach((item: unknown) => {
                        if (typeof item === 'object' && item !== null) {
                            const obj = item as Record<string, unknown>
                            const name = obj.name || obj.title || obj.statement || obj._id
                            if (name) allItems.push(String(name))
                        }
                    })
                }
            }

            extractNames(linkedItems.linkedValueMessages as unknown[])
            extractNames(linkedItems.linkedEvidence as unknown[])
            extractNames(linkedItems.linkedActivities as unknown[])
            extractNames(linkedItems.linkedKeyEvidenceStatements as unknown[])

            if (allItems.length > 0) {
                details.items = allItems
            }
        }
    }

    if (change.action === 'update' || change.action === 'create' || change.action === 'delete') {
        if (oldObj?.content !== undefined || newObj?.content !== undefined) {
            details.oldValue = oldObj?.content as string | undefined
            details.newValue = newObj?.content as string | undefined
        }
    }

    if (change.action === 'reorder') {
        const oldSlotOrder = oldObj?.slotOrder as SlotOrderItem[] | undefined
        const newSlotOrder = newObj?.slotOrder as SlotOrderItem[] | undefined

        if (oldSlotOrder && Array.isArray(oldSlotOrder)) {
            details.reorderBefore = oldSlotOrder.map((slot) => slot.content || 'Untitled')
        }
        if (newSlotOrder && Array.isArray(newSlotOrder)) {
            details.reorderAfter = newSlotOrder.map((slot) => slot.content || 'Untitled')
        }
    }

    // Archive action - show archived note if present
    if (change.action === 'archive' && newObj?.archivedNote) {
        details.items = [`Note: ${newObj.archivedNote as string}`]
    }

    // Clone action - show target column
    if (change.action === 'clone' && (metadata?.clonedToTitle || newObj?.clonedToTitle)) {
        const targetColumn = metadata?.clonedToTitle || (newObj?.clonedToTitle as string)
        details.items = [`Cloned to: ${targetColumn}`]
    }

    // Declone action - show what it was cloned from
    if (change.action === 'declone' && oldObj?.wasCloneOf) {
        details.items = [`Separated from parent cell`]
    }

    // For cell actions that don't show content diff, show the cell content from metadata
    const actionsWithoutContentDiff = [
        'highlight',
        'unhighlight',
        'lock',
        'unlock',
        'clone',
        'declone',
        'link',
        'unlink',
    ]
    if (change.entityType === 'TPPCell' && actionsWithoutContentDiff.includes(change.action) && metadata?.cellContent) {
        details.cellContent = metadata.cellContent
    }

    return details
}
